import React, { forwardRef, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Select, Checkbox } from "components/ui";

import TableRowSkeleton from "./loaders/TableRowSkeleton";
import Loading from "./Loading";
import { useTable, usePagination, useSortBy, useRowSelect } from "react-table";
import Pagination from "components/custom/pagination/pagination";

const { Tr, Th, Td, THead, TBody, Sorter } = Table;

const IndeterminateCheckbox = forwardRef((props, ref) => {
  const {
    indeterminate,
    onChange,
    onCheckBoxChange,
    onIndeterminateCheckBoxChange,
    ...rest
  } = props;

  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  const handleChange = (e) => {
    onChange(e);
    onCheckBoxChange?.(e);
    onIndeterminateCheckBoxChange?.(e);
  };

  return (
    <Checkbox
      className="mb-0"
      ref={resolvedRef}
      onChange={(_, e) => handleChange(e)}
      {...rest}
    />
  );
});

const DataTable = (props) => {
  const {
    skeletonAvatarColumns,
    columns,
    data,
    loading,
    onCheckBoxChange,
    onIndeterminateCheckBoxChange,
    onPaginationChange,
    onSelectChange,
    onSort,
    pageSizes,
    selectable,
    skeletonAvatarProps,
    pagingData,
    autoResetSelectedRows,
    showPagination,
  } = props;

  const { pageSize = 10, pageIndex, total } = pagingData;
  const pageCount = Math.ceil(total / pageSize);
  const pageSizeOption = useMemo(
    () =>
      pageSizes.map((number) => ({ value: number, label: `${number} / page` })),
    [pageSizes]
  );
console.log("total:", total, "pageCount:", pageCount);
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns,
        data,
        manualPagination: true,
        manualSortBy: true,
        autoResetSelectedRows,
      },
      useSortBy,
      usePagination,
      useRowSelect,
      (hooks) => {
        if (selectable) {
          hooks.visibleColumns.push((columns) => [
            {
              id: "selection",
              Header: (props) => (
                <div className="flex items-center justify-center">
                  <IndeterminateCheckbox
                    {...props.getToggleAllRowsSelectedProps()}
                    onIndeterminateCheckBoxChange={(e) =>
                      onIndeterminateCheckBoxChange?.(
                        e.target.checked,
                        props.rows
                      )
                    }
                  />
                </div>
              ),
              Cell: ({ row }) => (
                <div className="flex items-center justify-center">
                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                    onCheckBoxChange={(e) =>
                      onCheckBoxChange?.(e.target.checked, row.original)
                    }
                  />
                </div>
              ),
              sortable: false,
              width: 50,
            },
            ...columns,
          ]);
        }
      }
    );

  const handleSort = (column) => {
    if (!loading && column.sortable !== false) {
      const { id, isSortedDesc, toggleSortBy, clearSortBy } = column;
      const sortOrder = isSortedDesc ? "desc" : "asc";
      toggleSortBy(!isSortedDesc);
      onSort?.({ order: sortOrder, key: id }, { id, clearSortBy });
    }
  };

  return (
    <Loading loading={loading && data.length !== 0} type="cover">
      {/* Table Container with improved UI */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm bg-white dark:bg-gray-800">
        <Table {...getTableProps()} className="w-full">
          <THead className="bg-gray-50 dark:bg-gray-700/50">
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th
                    {...column.getHeaderProps()}
                    className="py-4 px-5 text-left font-bold uppercase tracking-wider text-gray-500 dark:text-gray-300 text-[10px] whitespace-nowrap"
                  >
                    {column.sortable !== false ? (
                      <div
                        className="flex items-center cursor-pointer gap-1"
                        onClick={() => handleSort(column)}
                      >
                        {column.render("Header")}
                        <Sorter sort={column.isSortedDesc} />
                      </div>
                    ) : (
                      <div>{column.render("Header")}</div>
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </THead>

          {loading && data.length === 0 ? (
            <TableRowSkeleton
              columns={columns?.length + (selectable ? 1 : 0)}
              rows={pageSize}
              avatarInColumns={skeletonAvatarColumns}
              avatarProps={skeletonAvatarProps}
            />
          ) : data.length === 0 ? (
            <TBody>
              <Tr>
                <Td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="py-24 text-center"
                >
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <span className="text-gray-400 text-4xl">∅</span>
                    <p className="text-gray-500 font-medium">
                      No records found
                    </p>
                  </div>
                </Td>
              </Tr>
            </TBody>
          ) : (
            <TBody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <Tr
                    {...row.getRowProps()}
                    className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                  >
                    {row.cells.map((cell) => (
                      <Td
                        {...cell.getCellProps()}
                        className="py-4 px-5 text-gray-600 dark:text-gray-300 whitespace-nowrap"
                      >
                        {cell.render("Cell")}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </TBody>
          )}
        </Table>
      </div>

      {/* Improved Pagination Section */}
      {!showPagination && (
        <div className="flex items-center justify-between mt-4">
        <Pagination
          pageCount={pageCount}
         pageValue={pageIndex} // react-paginate expects 0-indexed forcePage
         setPage={(selectedZeroIndexedPage) => {
  const newPage = selectedZeroIndexedPage + 1;

  if (newPage !== pageIndex) {
    onPaginationChange?.(newPage);
  }
}}
        />
          <div style={{ minWidth: 130 }}>
            <Select
              size="sm"
              menuPlacement="top"
              isSearchable={false}
              value={pageSizeOption.filter(
                (option) => option.value === pageSize
              )}
              options={pageSizeOption}
              onChange={(option) => onSelectChange?.(Number(option.value))}
            />
            
          </div>
        </div>
      )}
    </Loading>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onCheckBoxChange: PropTypes.func,
  onIndeterminateCheckBoxChange: PropTypes.func,
  onPaginationChange: PropTypes.func,
  onSelectChange: PropTypes.func,
  onSort: PropTypes.func,
  pageSizes: PropTypes.arrayOf(PropTypes.number),
  selectable: PropTypes.bool,
  skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
  skeletonAvatarProps: PropTypes.object,
  pagingData: PropTypes.shape({
    total: PropTypes.number,
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  autoResetSelectedRows: PropTypes.bool,
  showPagination: PropTypes.bool,
};

DataTable.defaultProps = {
  pageSizes: [10, 25, 50, 100],
  pagingData: {
    total: 0,
    pageIndex: 1,
    pageSize: 10,
  },
  data: [],
  columns: [],
  selectable: false,
  loading: false,
  autoResetSelectedRows: true,
  showPagination: false,
};

export default DataTable;
